import React, {useEffect} from 'react';
import {
  abandoned,
  downloadGrey,
  enlargeIc,
  failed,
  inProgress,
  noSearchResults,
  successful,
  toolTip,
} from '../../assets/images/images';
import {mapToOptionsArray, parseUrlParams} from '../../helpers';
import {shortenLargeNumbers} from '../../helpers/shortenLargeNumbers';
import {SkeletonLoader} from '../../components';

const ExpandImageToggle = ({resizeImage, imgUrl, id, customFunc}) => (
  <div className="absolute flex items-center gap-2 right-2 bottom-2">
    <button
      onBlur={() => (customFunc ? customFunc(imgUrl) : resizeImage(`${id}`))}
      onClick={() =>
        customFunc ? customFunc(imgUrl) : resizeImage(`${id}`, true)
      }
      // onClick={() => resizeImage(`${id}`, true)}
      className="flex items-start gap-2 px-2 py-2 text-xs font-medium rounded text-grey bg-white80 w-fit"
    >
      <span className="-mb-[2px]">Enlarge image</span>
      <img src={enlargeIc} alt="" className="-mt-[2px]" />
    </button>
    <a
      href={`${imgUrl}`}
      download="file"
      rel="noreferrer"
      target="_blank"
      className="flex items-start gap-2 px-2 py-2 text-xs font-medium rounded text-grey bg-white80 w-fit"
    >
      <span>Download</span>
      <img src={downloadGrey} alt="" className="-mt-[2px]" />
    </a>
  </div>
);

const defaultFilterOptions = [
  {
    title: 'Apps',
    name: 'app_ids',
    values: [],
  },
  {
    title: 'Status',
    name: 'status',
    values: [],
  },
  {
    title: 'Verification type',
    name: 'type',
    values: [],
  },
  {
    title: 'Verification mode',
    name: 'mode',
    values: [],
  },
];

function useClearSearchOnBackButton(clearSearch) {
  useEffect(() => {
    const handleBackButton = () => {
      clearSearch();
    };
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, [clearSearch]);
}

function NoSearchResults() {
  return (
    <div className="flex flex-col gap-4 items-center justify-center mt-[13%]">
      <img
        src={noSearchResults}
        alt="no search results"
        width={145}
        height={116}
      />
      <p className="text-body">No result found</p>
    </div>
  );
}

const getFilterOptions = userApps => {
  if (!userApps) {
    return defaultFilterOptions;
  }

  return [
    {
      title: 'Apps',
      name: 'app_ids',
      values: mapToOptionsArray(userApps, 'value'),
    },
    {
      title: 'Status',
      name: 'status',
      values: [
        {label: 'Successful', value: 'Completed', name: 'Completed'},
        {label: 'Failed', value: 'Failed', name: 'Failed'},
        {label: 'Pending', value: 'Pending', name: 'Pending'},
        {label: 'Ongoing', value: 'Ongoing', name: 'Ongoing'},
        {label: 'Started', value: 'Started', name: 'Started'},
        {label: 'Abandoned', value: 'Abandoned', name: 'Abandoned'},
      ],
    },
    {
      title: 'Verification type',
      name: 'type',
      values: [
        {label: 'BVN', value: 'BVN', name: 'BVN'},
        {label: 'NIN', value: 'NIN', name: 'NIN'},
        {label: 'Mobile', value: 'MOBILE', name: 'MOBILE'},
        {label: "Driver's License", value: 'DL', name: 'DL'},
        {label: 'Passport ID', value: 'PASSPORT_ID', name: 'PASSPORT_ID'},
        {label: "Driver's License ID", value: 'DL_ID', name: 'DL_ID'},
      ],
    },
    {
      title: 'Verification mode',
      name: 'mode',
      values: [
        {label: 'Liveness', value: 'LIVENESS', name: 'Liveness'},
        {label: 'OTP', value: 'OTP', name: 'OTP'},
      ],
    },
  ];
};

const handleSetIds = verification => {
  localStorage.setItem('referenceId', verification?.verification_id);
  localStorage.setItem('verificationId', verification?.verification_id);
  localStorage.setItem('verificationId', verification?.verification_id);
  localStorage.setItem('appId', verification?.app_id);
  localStorage.setItem('sessionId', verification?.session_id);
  localStorage.setItem(
    'refId',
    (verification?.verification_id && verification?.verification_id) || '-',
  );
};
const getParams = ({
  limit,
  setCurrentPage,
  currentPage,
  appIds,
  setSearchText,
  setFiltersApplied,
}) => {
  const apiParams = parseUrlParams();
  apiParams.limit = limit;
  apiParams.status = apiParams.status
    ?.split(',')
    ?.map(s => s?.charAt(0)?.toUpperCase() + s?.slice(1))
    ?.toString();

  const searchParams = new URLSearchParams(document.location.search);
  const pageParam = searchParams.get('page');
  if (pageParam) {
    setCurrentPage(parseInt(pageParam));
    apiParams.page = parseInt(pageParam);
  } else {
    apiParams.page = currentPage;
  }

  const appIdsFromUrl = searchParams.get('app_ids');
  if (appIdsFromUrl) {
    apiParams.appIds = appIdsFromUrl;
  } else {
    apiParams.appIds = appIds;
  }

  const searchTermFromUrl = searchParams.get('search');
  if (searchTermFromUrl) {
    setSearchText(searchTermFromUrl);
    apiParams.term = searchTermFromUrl;
  } else {
    apiParams.term = '';
  }
  if (
    Object.keys(apiParams).length === 2 &&
    'limit' in apiParams &&
    'page' in apiParams
  ) {
    setFiltersApplied(false);
  } else {
    setFiltersApplied(true);
  }
  return apiParams;
};
const getAnalysisPercentages = stats => [
  {
    color: '#87D4DB',
    label: 'successful',
    percentage: `${stats?.Completed || 0}%`,
  },
  {color: '#F15D5F', label: 'failed', percentage: `${stats?.Failed || 0}%`},
  {
    color: '#2A5392',
    label: 'abandoned',
    percentage: `${stats?.Abandoned || 0}%`,
  },
  {
    color: '#73739B',
    label: 'In progress',
    percentage: `${stats?.Pending + stats?.Ongoing || 0}%`,
  },
];

const getFlows = (card = {}) => [
  {
    icon: successful,
    alt: 'successful',
    title: 'Successful',
    value: shortenLargeNumbers(card?.Completed || 0, null, {
      startAt: 'M',
      decimals: 0,
    }),
  },
  {
    icon: failed,
    alt: 'failed',
    title: 'Failed',
    value: shortenLargeNumbers(card?.Failed || 0, null, {
      startAt: 'M',
      decimals: 0,
    }),
  },
  {
    icon: inProgress,
    alt: 'in-progress',
    title: 'In Progress',
    value: shortenLargeNumbers(card?.Pending + card?.Ongoing || 0, null, {
      startAt: 'M',
      decimals: 0,
    }),
  },
  {
    icon: abandoned,
    alt: 'abandoned',
    title: 'Abandoned',
    value: shortenLargeNumbers(card?.Abandoned || 0, null, {
      startAt: 'M',
      decimals: 0,
    }),
  },
];

const renderFlowCards = ({
  cards,
  isLoading: overviewLoading,
  conversionRate,
}) => {
  const flows = getFlows(cards);
  const formatValue = value => new Intl.NumberFormat('en-US').format(value);

  return flows.map(({title, value}) => (
    <div
      key={title}
      className="bg-white border py-4 px-6 border-[#E4E7EC] shadow-cards relative flex-wrap rounded-xl flex items-center gap-2 lg:flex-col lg:items-start xl:flex-row xl:items-center"
    >
      <div className="flex flex-col flex-wrap gap-4">
        <p className="text-sm text-[#667185] font-medium">{title}</p>
        {overviewLoading || !value ? (
          <SkeletonLoader style={{width: 150, height: 25, marginBottom: 10}} />
        ) : (
          <h1 className="text-[32px] font-semibold text-[#1D2739]">
            {formatValue(+value)}
          </h1>
        )}
      </div>
      {title === 'In Progress' && (
        <div className="absolute bottom-[14px] right-[24px] group">
          <img src={toolTip} alt="tooltip" className="cursor-pointer" />
          <div className="hidden group-hover:block w-[229px] bg-grey-50 h-[55px] absolute bottom-full text-center top-[25px] right-0  text-[#5C688E] text-[12px] rounded p-2">
            This is a combination of Pending and Ongoing Verifications
          </div>
        </div>
      )}
      {title === 'Successful' && (
        <div className="absolute bottom-1 right-[24px]">
          <div className="w-full  bottom-full text-center text-[8px] lg:text-[12px] right-0  text-[#5C688E] rounded ">
            Conversion Rate :
            <span className="text-grey ml-2">{`${conversionRate || 0}%`}</span>
          </div>
        </div>
      )}
    </div>
  ));
};
const renderStatusDistribution = ({stats = {}}) => {
  const analysisPercentages = getAnalysisPercentages(stats);
  return analysisPercentages.map(({color, label, percentage}) => (
    <div key={label} className="flex items-center justify-between w-full">
      <div className="flex items-center gap-1.5">
        <div
          style={{backgroundColor: color}}
          className="w-2 h-2 rounded-full"
        />{' '}
        <p className="text-deepBlue text-sm font-normal capitalize">{label}</p>
      </div>
      <p className="text-sm text-[#858DAA] font-medium">{percentage}</p>
    </div>
  ));
};

const processData = data =>
  data?.map(item => ({
    date: item?.verification_date,
    Completed: item.Completed,
    Failed: item.Failed,
    Pending: item.Pending,
    Abandoned: item.Abandoned,
  }));
const processBAASData = data =>
  data?.map(item => ({
    date: item?.transaction_date,
    inFlow: item?.deposit,
    outFlow: item?.transfer,
  }));
function getAlphabet(number) {
  if (number < 1 || number > 26 || !Number.isInteger(number)) {
    return number;
  }

  // Convert the number to the corresponding alphabet
  // 'A' has ASCII code 65, so we add (number - 1) to get the desired letter
  return String.fromCharCode(64 + number);
}

const checkAllowedStatusReasons = status =>
  ['Failed', 'Pending', 'Abandoned'].includes(status);

const getCommentDisplay = verification => {
  const MAX_LENGTH = 35;
  const isAllowedReason = checkAllowedStatusReasons(verification?.status);
  const comment = verification?.review_comment;

  if (isAllowedReason && comment && !['widget_closed', ''].includes(comment)) {
    const isLong = comment.length > MAX_LENGTH;
    const mainText = isLong
      ? `${comment.substring(0, MAX_LENGTH)}...`
      : comment;

    return {
      mainText: mainText,
      fullText: isLong ? comment : null,
      showTooltip: isLong,
    };
  }

  return {
    mainText:
      verification?.status === 'Completed' ? 'Successful Verification' : '-',
    fullText: null,
    showTooltip: false,
  };
};

export {
  processData,
  processBAASData,
  getFlows,
  defaultFilterOptions,
  useClearSearchOnBackButton,
  NoSearchResults,
  getFilterOptions,
  handleSetIds,
  getParams,
  renderFlowCards,
  renderStatusDistribution,
  getAlphabet,
  ExpandImageToggle,
  getCommentDisplay,
};
